<template>
  <div class="i-radio-buttons i-destination-picker" :class="vertical">
    <div class="field-label" v-if="label && optionFormatted.length > 0">
      {{ label }}
    </div>

    <div class="radio-options" v-for="option in optionFormatted" :key="option.value">
      <label :class="{'disabled-label' : option.disabled}">
        <input
          type="radio"
          :name="name"
          :checked="option.value === value"
          :value="option.value"
          :disabled="option.disabled"
          @input="onInput"
        />
        {{ option.label }}
      </label>
      <span
        class="submit-icon"
        :class="{'disabled-icon': !hasAccessRights(['complete-access', 'system-access', 'sub-admin-access'])}"
        @click="openDeleteDialog(option.id)">
        <i-generate-image
          image="icons/cancel-icon.svg"
        />
      </span>
    </div>

    <i-button
      class="submit-button border bold setup-new-connection-btn"
      :label="$t('settings.connections.newConnection')"
      :is-disabled="!hasAccessRights(['complete-access', 'system-access', 'sub-admin-access'])"
      @click.stop.prevent="setupNewConnection()"
    />

    <form-builder
      v-if="showSetup"
      :value="formData"
      :button-label="$t('common.connect')"
      :config="globalSetupConnection"
      :submit-action="submitAction"
    />

    <div class="field-hint" v-if="hint">
      {{ hint }}
    </div>

    <i-confirmation-dialog
      ref="deleteConnectionModal"
      :title="$t('settings.connections.deleteConnection')"
      class="delete-confirmation-dialog"
      :confirm-action="deleteConnection"
    >
      <template v-slot:idialog-body>
        {{ $t('settings.connections.deleteConnectionsConfirm') }}
      </template>
    </i-confirmation-dialog>

    <i-loader :is-loading="isLoading || isConnecting"/>
  </div>
</template>

<script>
import IGenerateImage from "@/components/common/IGenerateImage.vue";
import IButton from "@/components/common/IButton.vue";
import {getSetupConnectionConfig, setupConnection} from "@/services/forms/app/recovery/export"
import IConfirmationDialog from "@/components/common/dialog/IConfirmationDialog.vue";
import {mapGetters} from "vuex";
import ILoader from "@/components/common/ILoader.vue";

export default {
  name: "IDestinationPicker",
  emits: ['input', 'refresh'],
  components: {ILoader, IConfirmationDialog, IButton, IGenerateImage},
  props: {
    data: {
      type: Object
    },
    isLoading: {
      type: Boolean
    },
    label: {
      type: String
    },
    hint: {
      type: String,
    },
    name: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean
    },
    vertical: {
      type: String, // horizontal|vertical
    },
    storageDestinationType: {
      type: [String, Number],
    }
  },

  data() {
    return {
      isConnecting: false,
      showSetup: false,
      formData: null,
      connectionId: null,
      regionOption: []
    }
  },
  computed: {
    ...mapGetters({
      getFormatedRegions: "salesforce/recovery/salesforce/getListRegions",
      getFormatedRegionsGCP: "salesforce/recovery/salesforce/getListRegionsGcp",
    }),
    globalSetupConnection() {
      return setupConnection(this.$t.bind(this), this.storageDestinationType, this.storageDestinationType == 8 ? this.getFormatedRegionsGCP() : this.getFormatedRegions(), this.$t('menu.region'))
    },
    optionFormatted() {
      return this.options
        .filter(option => option.isActive === true)
        .map(option => ({
          label: this.getOptionLabel(option),
          value: this.getOptionValue(option),
          id: option.id,
          disabled: option.disabled
        }))
    }
  },
  methods: {
    onInput(e) {
      this.$emit('input', e.target.value);
    },
    openDeleteDialog(id) {
      if (this.hasAccessRights(['complete-access', 'system-access', 'sub-admin-access'])) {
        this.connectionId = id;
        this.$refs['deleteConnectionModal'].openModal();
      }
    },
    deleteConnection() {
      return this.$store.dispatch(`salesforce/recovery/salesforce/deleteConnection`, {id: this.connectionId})
        .then(() => {
          this.$emit('refresh')
        })
    },
    setupNewConnection() {
      const {isOauth, actionName} = getSetupConnectionConfig(this.storageDestinationType);

      if (isOauth) {
        return this.$store.dispatch(actionName, {ProviderTenantType: this.$providerTenantVal})
          .then((data) => {
            window.open(data.authorizeUrl);
          });
      } else {
        this.showSetup = !this.showSetup;

        return this.$store.dispatch(actionName)
          .then((data) => {
            this.regionOption = data;
          });
      }
    },
    wathcForParentMessage(e) {
      try {
        const responseObj = JSON.parse(e.data);
        if (responseObj.success === true) {
          this.$notifySuccess(this.$t('settings.connections.connectionSuccess'));
          this.$emit('refresh');
        }
      } catch (e) {

      }
    },
    submitAction(formData) {
      this.isConnecting = true;

      this.formData = {
        ...formData,
        providerTenantType: this.$providerTenantVal,
        storageDestinationType: this.storageDestinationType,
      }

      const connectionModules = {
        1: 'connectAws',
        2: 'connectAzure',
        3: 'connectGoogle',
        4: 'connectMicrosoft',
        5: 'connectDropbox',
        6: 'connectBox',
        7: 'connectS3Compatible',
        8: 'connectGoogleCloud'
      };

      const connectionModule = connectionModules[this.storageDestinationType] || '';

      return this.$store.dispatch(`salesforce/recovery/salesforce/${connectionModule}`, this.formData)
        .then(() => {
          this.$notifySuccess(this.$t('settings.connections.connectionSuccess'));
          this.isConnecting = false;
          this.showSetup = false;
          this.formData = {};
          this.$emit('refresh')
        }).catch((e) => {
          this.$notifyError(this.$t('settings.connections.connectionFail'));
          this.isConnecting = false;
          this.showSetup = false;
          this.formData = {};
        })
    },
    getOptionLabel(option) {
      if ([3, 4, 5, 6].includes(option.storageDestinationType)) {
        return option.email;
      } else if (option.storageDestinationType == 1 || option.storageDestinationType == 7 || option.storageDestinationType == 8) {
        return option.bucketName;
      } else if (option.storageDestinationType == 2) {
        return option.containerName;
      }

      return this.$t('common.na');
    },
    getOptionValue(option) {
      return option.id;

      // if ([3, 4, 5, 6].includes(option.storageDestinationType)) {
      //   return option.id;
      // }
      // else if (option.storageDestinationType == 1) {
      //   return option.accessKey;
      // }
      // else if (option.storageDestinationType == 2) {
      //   return option.accountKey;
      // }

      // return this.$t('common.na');
    }
  },
  created() {
    window.addEventListener('message', this.wathcForParentMessage)
  },
  beforeUnmount() {
    window.removeEventListener('message', this.wathcForParentMessage)
  }
}
</script>

<style lang="scss">
@import "IDestinationPicker";
</style>
